import dayjs from "dayjs";
import { ListInvoiceState } from "./reducer";
import {
  ConsolidatedInvoiceExcelItem,
  ConsolidatedInvoicesExcelRequest,
  InvoiceDocument,
  InvoiceHeader,
} from "../../../../api/invoices/interfaces";
import {
  InvoiceByAccountWithFiltersConsolidationRequest,
  InvoiceByFiltersConsolidationRequest,
} from "../../../../api/jobsService/IJobsService";
import { RBACRole } from "../../../../api/rbac/types";
import {
  ALL_ACCOUNTS,
  ARGENTINA_DEALER_LOCATION,
  BRAZIL_DEALER_LOCATION,
  BRAZIL_EXPORT_MRUS,
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_FILTER,
  MEXICO_DEALER_LOCATION,
} from "../../../../utils/constants";
import { getHashParametersFromURL } from "../../../../utils/invoiceQueryParameters";
import { getPendingDownloadJobs } from "../../../../utils/notificationsUtils";
import { getShowClaimStatus } from "../../../../utils/showClaims";
import { isUserAuthorizedToCreateReturn } from "../../../../utils/showReturns";
import { isAuthorizedForMultipleAccounts } from "../../../preferences/utils";
import { commonDateFormat, dayJSLang } from "../../../returns/utils";
import {
  FilterType,
  InvoicesFilterState,
  InvoicesRows,
} from "../invoicesGrid/types";

const COUNTRY_CODES = {
  MEXICO: "MX",
  UNITED_STATES: "US",
  UNITED_KINGDOM: "GB",
  CHINA: "CN",
  CANADA: "CA",
  INDIA: "IN",
  BRAZIL: "BR",
};

export const getDealerAccounts = (state: ListInvoiceState): string => {
  const { selectedAccounts } = state;
  return isMultipleAccountSelected(state)
    ? selectedAccounts.map((acc) => acc.id).join(",")
    : state.dealerAccount?.id ?? "";
};

export const getDealerAccountsList = (state: ListInvoiceState): string[] => {
  const { selectedAccounts } = state;
  return isMultipleAccountSelected(state)
    ? selectedAccounts.map((acc) => acc.id)
    : [state.dealerAccount?.id ?? ""];
};

export const showNFENumber = (state: ListInvoiceState): boolean =>
  state.dealerLocation === COUNTRY_CODES.BRAZIL;

export const showFacturaNumber = (state: ListInvoiceState): boolean =>
  state.dealerLocation === ARGENTINA_DEALER_LOCATION;

export const showOfficialDocumentNumber = (state: ListInvoiceState): boolean =>
  BRAZIL_EXPORT_MRUS.includes(state.dealerAccount?.mruSalesOrg ?? "");

const isUSorCanandaDealer = (state: ListInvoiceState): boolean =>
  state.dealerLocation === COUNTRY_CODES.UNITED_STATES ||
  state.dealerLocation === COUNTRY_CODES.CANADA;

export const showViewedDetails = (state: ListInvoiceState): boolean =>
  isUSorCanandaDealer(state);

export const isMultipleAccountSelected = (state: ListInvoiceState): boolean =>
  state.isCorporateViewEnabled &&
  state.selectedAccounts.length > 1 &&
  isAuthorizedForMultipleAccounts(
    state.user,
    state.featureToggles.enableMultipleAccounts,
    state.featureToggles.enableCorporateViewDpaRole
  );

export const enableInvoiceConsolidationForCheckBox = (
  state: ListInvoiceState
): boolean => state.featureToggles.enableInvoiceConsolidation;

export const isReturnsAvailable = (state: ListInvoiceState): boolean =>
  state.rbacAccess.returns.createEnabled &&
  state.featureToggles.enableCreateReturns;

export const isAuthorizedToCreateReturns = (state: ListInvoiceState): boolean =>
  isUserAuthorizedToCreateReturn(
    state.isRBACEnabled,
    isReturnsAvailable(state)
  ) ?? false;

const isClaimsByHUNotEnabled = (
  state: ListInvoiceState,
  isViewAction: boolean
): boolean => !state.featureToggles.enableClaimsByHU || isViewAction;

const isUserPermissionAllowed = (
  state: ListInvoiceState,
  isViewAction: boolean,
  isCreateAction: boolean
): boolean =>
  (isViewAction && state.rbacAccess.claims.viewEnabled) ||
  (isCreateAction && state.rbacAccess.claims.createEnabled);

const vendorDirectValues = (state: ListInvoiceState) =>
  state.invoicesResponse?.invoices.map((invoice) => invoice.isVendorDirect);

export const hasAccessToClaim = (
  businessAction: {
    isViewAction: boolean;
    isCreateAction: boolean;
  },
  state: ListInvoiceState,
  isVendorDirect: (state: ListInvoiceState) => boolean
): boolean => {
  const claimsByHUNotEnabled = isClaimsByHUNotEnabled(
    state,
    businessAction.isViewAction
  );
  const userPermissionAllowed = isUserPermissionAllowed(
    state,
    businessAction.isViewAction,
    businessAction.isCreateAction
  );

  return (
    ((state.isRBACEnabled && userPermissionAllowed) ||
      (!state.isRBACEnabled && getShowClaimStatus())) &&
    state.featureToggles.enableClaims &&
    (claimsByHUNotEnabled || isVendorDirect(state))
  );
};

export const isClaimsEnabledForDealerLocation = (
  state: ListInvoiceState
): boolean => {
  if (state.dealerLocation === COUNTRY_CODES.INDIA) {
    return state.featureToggles.enableClaimsIndia;
  }
  if (state.dealerLocation === COUNTRY_CODES.CHINA) {
    return state.featureToggles.enableClaimsChina;
  }
  return true;
};

export const showActions = (state: ListInvoiceState): boolean =>
  isClaimsEnabledForDealerLocation(state) &&
  (isAuthorizedToCreateReturns(state) ||
    hasAccessToClaim(
      {
        isViewAction: false,
        isCreateAction: true,
      },
      state,
      (listInvoiceState) =>
        vendorDirectValues(listInvoiceState)?.some(
          (isVendorDirect) => isVendorDirect
        )
    ));

export const showDownloadAll = (state: ListInvoiceState): boolean =>
  !isMultipleAccountSelected(state);

export const showConsolidatedInvoice = (state: ListInvoiceState): boolean =>
  state.featureToggles.enableInvoiceConsolidation;

function isFilterDateRangeBelowOrEqualTo31Days(state: ListInvoiceState) {
  const dateBefore31Days = state.filter.filters.endDate.subtract(31, "days");
  return (
    dateBefore31Days.isBefore(state.filter.filters.startDate) ||
    dateBefore31Days.isSame(state.filter.filters.startDate)
  );
}

export const enableSelectAllPages = (state: ListInvoiceState): boolean =>
  isFilterDateRangeBelowOrEqualTo31Days(state);

function hasUnseenInvoices(state: ListInvoiceState) {
  return state.invoicesResponse.total !== state.invoicesResponse.totalViewed;
}

export const enableSelectAllNew = (state: ListInvoiceState): boolean =>
  hasUnseenInvoices(state) && isFilterDateRangeBelowOrEqualTo31Days(state);

export const enableSelectNone = (state: ListInvoiceState): boolean =>
  state.rowSelectionModel.length > 0;

export const selectAllPageTooltip = (state: ListInvoiceState): string =>
  isFilterDateRangeBelowOrEqualTo31Days(state)
    ? ""
    : "DATE_RANGE_GREATER_THAN_31_DAYS";

export const selectAllNewTooltip = (state: ListInvoiceState): string => {
  if (!isFilterDateRangeBelowOrEqualTo31Days(state)) {
    return "DATE_RANGE_GREATER_THAN_31_DAYS";
  }
  if (!hasUnseenInvoices(state)) {
    return "NO_NEW_INVOICES";
  }
  return "";
};

export const showHULink = (state: ListInvoiceState): boolean =>
  state.rbacAccess.handlingUnits.viewEnabled;

function isOrderInvoice(invoice: InvoiceHeader) {
  return invoice.invoiceType === "Order Invoice" && !invoice.reversed;
}

function isVendorDirectInvoice(
  invoice: InvoiceHeader,
  state: ListInvoiceState
) {
  return invoice.isVendorDirect && !state.featureToggles.enableClaims;
}

function hasActionsAccess(state: ListInvoiceState, invoice: InvoiceHeader) {
  return (
    isOrderInvoice(invoice) &&
    (isAuthorizedToCreateReturns(state) ||
      hasAccessToClaim(
        {
          isViewAction: false,
          isCreateAction: true,
        },
        state,
        (listInvoiceState) => isVendorDirectInvoice(invoice, listInvoiceState)
      ) ||
      isVendorDirectInvoice(invoice, state))
  );
}

export function getActionsOptions(state: ListInvoiceState) {
  const { isLoading: isActionsLoading, invoicesDetails } = state.actions;
  const { enableClaimsByHU } = state.featureToggles;
  const isVendorDirect = invoicesDetails?.isVendorDirect ?? false;

  return {
    isLoading: isActionsLoading,
    actions: {
      returns: {
        show:
          isAuthorizedToCreateReturns(state) &&
          (!enableClaimsByHU ||
            (isVendorDirect && state.featureToggles.enableVendorDirectReturn) ||
            !isVendorDirect) &&
          !isActionsLoading,
        enabled: !invoicesDetails?.timeLimit.returnsExceeded,
      },
      claims: {
        show:
          !isVendorDirect &&
          !enableClaimsByHU &&
          hasAccessToClaim(
            {
              isViewAction: false,
              isCreateAction: true,
            },
            state,
            () => isVendorDirect
          ) &&
          !isActionsLoading,
        enabled: !invoicesDetails?.timeLimit.claimsExceeded,
      },
      vendorDirect: {
        show:
          isVendorDirect &&
          hasAccessToClaim(
            { isViewAction: false, isCreateAction: true },
            state,
            () => isVendorDirect
          ) &&
          enableClaimsByHU &&
          !isUSorCanandaDealer(state) &&
          !isActionsLoading,
        enabled: !invoicesDetails?.timeLimit.claimsExceeded,
      },
    },
  };
}

const parentData = (
  id: string,
  invoice: InvoiceHeader,
  lang: string,
  state: ListInvoiceState
) => {
  const dealerAccountId = invoice.dealerAccount ?? "";
  const matchingAccount = state.selectedAccounts.find(
    (acc) => acc.id === dealerAccountId
  );

  return {
    id,
    invoiceNumber: invoice.invoiceNumber,
    nfeNumber: invoice.nfeNumber,
    factura: invoice.factura,
    officialDocumentNumber: invoice.officialDocumentNumber,
    documentNumber: invoice.documents[0].documentNumber,
    handlingUnit: invoice.documents[0].handlingUnit,
    dealerReference: invoice.documents[0].dealerReference,
    invoiceDate: dayjs(invoice.billingDate)
      .locale(dayJSLang[lang])
      .format(commonDateFormat),
    invoiceType: invoice.invoiceType,
    orderType: invoice.documents[0].orderType ?? invoice.orderType,
    viewedDate: invoice.viewedDate
      ? dayjs(invoice.billingDate)
          .locale(dayJSLang[lang])
          .format(commonDateFormat)
      : null,
    viewedBy: invoice.viewedBy && invoice.viewedBy.toUpperCase(),
    group: [invoice.invoiceNumber],
    isSeen: !!invoice.viewedBy,
    dealerAccount: dealerAccountId,
    mruSalesOrg: matchingAccount?.mruSalesOrg ?? "",
    docClass: invoice.docClass,
    isVendorDirect: invoice.isVendorDirect,
    isReversed: invoice.reversed,
    showNfeLink:
      state.dealerLocation === COUNTRY_CODES.BRAZIL &&
      invoiceTypesWithNfeAttachment.includes(invoice.invoiceType) &&
      !state.user.is(RBACRole.JDPS_SERVICE_DEALER),
    showActions: hasActionsAccess(state, invoice),
  };
};

const childData = (
  parentId: string,
  documents: InvoiceDocument[],
  parentGroup: string,
  isVendorDirect: boolean,
  viewedBy?: string
) =>
  documents.slice(1).map((document: InvoiceDocument) => ({
    id: `${parentId}.${document.documentNumber}.${document.handlingUnit}`,
    invoiceNumber: "",
    officialDocumentNumber: "",
    documentNumber: document.documentNumber,
    handlingUnit: document.handlingUnit,
    dealerReference: document.dealerReference,
    orderType: document.orderType,
    group: [parentGroup, `${document.documentNumber}.${document.handlingUnit}`],
    isSeen: !!viewedBy,
    dealerAccount: "",
    isVendorDirect,
    isReversed: false,
    showNfeLink: false,
    showActions: false,
  }));

export const getInvoiceRowData = (state: ListInvoiceState): InvoicesRows[] =>
  isTableLoading(state)
    ? []
    : state.invoicesResponse.invoices.flatMap((invoice) => [
        parentData(`${invoice.invoiceNumber}`, invoice, state.language, state),
        ...childData(
          `${invoice.invoiceNumber}`,
          invoice.documents,
          invoice.invoiceNumber,
          invoice.isVendorDirect,
          invoice.viewedBy
        ),
      ]);

const invoiceTypesWithNfeAttachment = ["Order Invoice", "Debit Memo"];

export const isDebouncedSearch = (searchField: FilterType): boolean =>
  [
    FilterType.InvoiceNumber,
    FilterType.NotaFiscalNumber,
    FilterType.DocumentNumber,
    FilterType.HandlingUnitNumber,
    FilterType.DealerReference,
    FilterType.OfficialDocumentNumber,
    FilterType.PartNumber,
    FilterType.DealerAccount,
    FilterType.Factura,
  ].includes(searchField);

export const getConsolidateDownloadInProgressStatus = (
  state: ListInvoiceState
) =>
  getPendingDownloadJobs(
    isMultipleAccountSelected(state)
      ? ALL_ACCOUNTS
      : state.dealerAccount?.id ?? ""
  ).some((job) =>
    [
      INVOICE_CONSOLIDATION,
      INVOICE_FILTER,
      INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
      INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
    ].includes(job.type)
  );

export const getDownloadingAllInProgressStatus = (state: ListInvoiceState) =>
  getPendingDownloadJobs(getDealerAccounts(state)).some(
    (job) => job.type === "Invoices"
  );

export const shouldDisableConsolidatedInvoiceDownloadButton = (
  state: ListInvoiceState
) => state.rowSelectionModel.length === 0 && !state.selectAllNew;

export const getRequestByFilter = (
  state: ListInvoiceState,
  multipleAccountsSelected = isMultipleAccountSelected(state)
):
  | InvoiceByAccountWithFiltersConsolidationRequest
  | InvoiceByFiltersConsolidationRequest => {
  const dealerAccounts = getDealerAccounts(state);
  const accountIdData = multipleAccountsSelected
    ? { accountIds: dealerAccounts.split(",") }
    : { accountId: dealerAccounts };

  return {
    ...accountIdData,
    invoiceNumber: state.filter.filters.invoiceNumber ?? undefined,
    nfeNumber: state.filter.filters.nfeNumber ?? undefined,
    officialDocumentNumber:
      state.filter.filters.officialDocumentNumber ?? undefined,
    documentNumber: state.filter.filters.documentNumber ?? undefined,
    dealerReference: state.filter.filters.dealerReference ?? undefined,
    startDate: state.filter.filters.startDate.toISOString().split("T")[0],
    endDate: state.filter.filters.endDate.toISOString().split("T")[0],
    invoiceType: state.filter.filters.invoiceType,
    ...(state.filter.filters.srcProcessType && {
      srcProcessType: state.filter.filters.srcProcessType,
    }),
    partNumber: state.filter.filters.partNumber ?? undefined,
    handlingUnit: state.filter.filters.handlingUnitNumber ?? undefined,
    factura: state.filter.filters.factura ?? undefined,
    invoiceRemoveList: state.consolidatedInvoices.exclude,
    invoiceIncludeList: state.consolidatedInvoices.include,
    allNew: state.selectAllNew,
  };
};

export const getRequestByNumbers = (state: ListInvoiceState) => {
  const selectedInvoices = state.invoicesResponse.invoices.filter((invoice) =>
    state.rowSelectionModel.includes(invoice.invoiceNumber)
  );

  if (isMultipleAccountSelected(state)) {
    const accountIdToInvoiceNumbers = selectedInvoices.reduce(
      (acc, invoice) => {
        const accountId = invoice.dealerAccount ?? "";
        if (!acc[accountId]) acc[accountId] = [];
        acc[accountId].push(invoice.invoiceNumber);
        return acc;
      },
      {} as { [key: string]: string[] }
    );

    return {
      accountIdToInvoiceNumbers: Object.entries(accountIdToInvoiceNumbers).map(
        ([accountId, invoiceNumbers]) => ({ accountId, invoiceNumbers })
      ),
    };
  }

  return {
    accountId: getDealerAccounts(state),
    invoiceNumbers: selectedInvoices.map((invoice) => invoice.invoiceNumber),
  };
};

export const getConsolidatedInvoicesExcelRequest = (
  state: ListInvoiceState
): ConsolidatedInvoicesExcelRequest => {
  const accountInvoicesMap: Record<string, ConsolidatedInvoiceExcelItem[]> = {};

  state.invoicesResponse.invoices
    .filter((invoice) =>
      state.rowSelectionModel.includes(invoice.invoiceNumber)
    )
    .forEach((invoice) => {
      const accountId = invoice.dealerAccount || state.dealerAccount?.id || "";
      if (!accountInvoicesMap[accountId]) accountInvoicesMap[accountId] = [];
      accountInvoicesMap[accountId].push({
        invoiceNumber: invoice.invoiceNumber,
        invoiceType: invoice.invoiceType,
        nfe: invoice.nfeNumber ?? "",
        originalNfe: invoice.originalNfeNumber ?? "",
      });
    });

  return {
    ...accountInvoicesMap,
    dealerLocation: state.dealerLocation ?? "",
  };
};

export const shouldDisableConsolidateInvoicesToExcelButton = (
  state: ListInvoiceState
) => state.rowSelectionModel.length === 0;

export const isBrazilExportDealer = (state: ListInvoiceState): boolean =>
  BRAZIL_EXPORT_MRUS.includes(state.dealerAccount?.mruSalesOrg || "");

export const getlegacyInvoicesUrl = (
  state: ListInvoiceState,
  JDPOINT_INVCS_URL: string
): string => {
  const accountId = getDealerAccounts(state);
  const accountIdParam = accountId ? `?account=${accountId}` : "";
  return `${JDPOINT_INVCS_URL}/partsinvoices/JDPointInvoice/allInvoices.con${accountIdParam}`;
};

export function isAuthenticationError(state: ListInvoiceState): boolean {
  return (
    state.error?.response?.data?.statusCode === 401 ||
    state.error?.response?.status === 401 ||
    state.error?.response?.data?.statusCode === 403 ||
    state.error?.response?.status === 403
  );
}

export function isTableLoading(state: ListInvoiceState): boolean {
  return state.isLoading && state.isInvoicesLoaded;
}

export function isTechnicalError(state: ListInvoiceState): boolean {
  return state.error !== undefined && !isAuthenticationError(state);
}

export function isLoading(state: ListInvoiceState): boolean {
  return state.isLoading && !state.isInvoicesLoaded;
}

export function hasError(state: ListInvoiceState): boolean {
  return isAuthenticationError(state) || isTechnicalError(state);
}

export function showInvoicesGrid(state: ListInvoiceState) {
  return !isLoading(state) && !hasError(state);
}

export function showPaginationFooter(state: ListInvoiceState): boolean {
  return !isTableLoading(state) && state.invoicesResponse.total > 10;
}

export const areInvoiceFilterQueryParametersSameAsFilter = (
  currentFilterState: InvoicesFilterState
) => {
  const queryParamFilters = ["documentNumber", "startDate", "endDate"];
  const hashParameters = getHashParametersFromURL();

  if (Object.keys(hashParameters).length === 0) {
    return false;
  }

  return !Object.keys(currentFilterState).some((key) => {
    // @ts-ignore
    if (!queryParamFilters.includes(key) && currentFilterState[key] !== "") {
      return true;
    }
    if (queryParamFilters.includes(key)) {
      const currentFilterValue =
        // @ts-ignore
        typeof currentFilterState[key] === "string"
          ? // @ts-ignore
            currentFilterState[key]
          : // @ts-ignore
            currentFilterState[key]?.toISOString().split("T")[0];
      // @ts-ignore
      if (currentFilterValue !== hashParameters[key]) {
        return true;
      }
    }
    return false;
  });
};

export const getGridFilter = (state: ListInvoiceState) => {
  if (state.filter.sortField === "billingDate") {
    return {
      ...state.filter,
      sortField: "invoiceDate",
    };
  }
  return state.filter;
};

export const isBrazilDealer = (dealerLocation: string | undefined) =>
  BRAZIL_DEALER_LOCATION === dealerLocation;
export const isMexicoDealer = (dealerLocation: string | undefined) =>
  MEXICO_DEALER_LOCATION === dealerLocation;
